import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { User, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, Eye, Edit, Trash2 } from 'lucide-react';
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import MainService from '../../services/MainService';
import { useNavigate } from 'react-router-dom';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';

const CUSTOMER_COLUMNS = [
  { field: 'name', title: 'Name', widthClass: 'w-[12%]', hideable: true },
  { field: 'mobileno1', title: 'Mobile No1', widthClass: 'w-[10%]', hideable: true },
  { field: 'mobileno2', title: 'Mobile No2', widthClass: 'w-[10%]', hideable: true },
  { field: 'telno', title: 'Tel No', widthClass: 'w-[9%]', hideable: true },
  { field: 'emailid', title: 'Email', widthClass: 'w-[12%]', hideable: true },
  { field: 'address', title: 'Address', widthClass: 'w-[12%]', hideable: true },
  { field: 'emiratename', title: 'Emirate', widthClass: 'w-[9%]', hideable: true },
  { field: 'areaname', title: 'Area', widthClass: 'w-[9%]', hideable: true },
  { field: 'locationname', title: 'Location', widthClass: 'w-[10%]', hideable: true },
  { field: 'actions', title: 'Options', widthClass: 'w-[8%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = () =>
  CUSTOMER_COLUMNS.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const getCustomerCellValue = (row, field) => {
  switch (field) {
    case 'name': return row.Name;
    case 'mobileno1': return row.MobileNo1;
    case 'mobileno2': return row.MobileNo2;
    case 'telno': return row.TelNo;
    case 'emailid': return row.EmailId;
    case 'address': return row.Address;
    case 'emiratename': return row.EmirateName;
    case 'areaname': return row.AreaName;
    case 'locationname': return row.LocationName;
    default: return row[field];
  }
};

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(defaultVisibility);
  const [groupField, setGroupField] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchCustomers = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    search: searchArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const search = searchArg !== undefined ? searchArg : activeSearchKeyword;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        Keyword: (search || '').trim() || null,
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService.CustomersList(body);
      if (seq !== loadSeqRef.current) return;
      setCustomers(Array.isArray(response?.Data) ? response.Data : []);
      setTotalCount(Number(response?.Total) || 0);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
      setCustomers([]);
      setTotalCount(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, activeSearchKeyword, sortField, sortDirection, filtersByField]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => CUSTOMER_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows: customers }];
    const mapped = customers.map((r) => ({ ...r, [groupField]: getCustomerCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [customers, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
    }
  };

  const handleSearch = () => {
    const keyword = searchTerm.trim();
    setPage(1);
    setActiveSearchKeyword(keyword);
    fetchCustomers({ page: 1, search: keyword });
  };

  const handleRefresh = () => {
    setPage(1);
    setSearchTerm('');
    setActiveSearchKeyword('');
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchCustomers({
      page: 1,
      search: '',
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchCustomers({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchCustomers({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchCustomers({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = CUSTOMER_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchCustomers({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchCustomers({ page: 1, filters: nextFilters });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Customers',
        fileName: `customers_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', 'Name', 'Mobile No1', 'Mobile No2', 'Tel No', 'Email', 'Address', 'Emirate', 'Area', 'Location'],
        groups: displayGroups,
        groupFieldTitle: CUSTOMER_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (customer, serial) => [
          serial,
          customer.Name,
          customer.MobileNo1,
          customer.MobileNo2,
          customer.TelNo,
          customer.EmailId,
          customer.Address,
          customer.EmirateName,
          customer.AreaName,
          customer.LocationName,
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setError('No customers to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  const handleContextMenu = (event, customer) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setContextMenu(null);
    setSelectedCustomer(null);
  };

  const handleView = (customer) => {
    handleMenuClose();
    console.log('View customer:', customer);
  };

  const handleEdit = (customer) => {
    handleMenuClose();
    if (customer?.Id) navigate(`/home/accounts/addCustomer/${customer.Id}`);
  };

  const handleDelete = (customer) => {
    handleMenuClose();
    console.log('Delete customer:', customer);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="mb-2 flex items-center">
              <User className="mr-2 h-6 w-6 text-primary" />
              <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
            </div>
            <Button
              variant="contained"
              startIcon={<User className="h-4 w-4" />}
              onClick={() => navigate('/home/accounts/addCustomer')}
              sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
            >
              Add Customer
            </Button>
          </div>

          <div className="flex flex-col items-end gap-4 lg:flex-row">
            <div className="flex-1">
              <TextField
                fullWidth
                placeholder="Search by name or mobile number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="h-5 w-5 text-gray-400" />
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none', borderBottom: '1px solid #E5E7EB' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderBottom: '2px solid #46955A' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderBottom: '2px solid #46955A' },
                  },
                }}
                variant="outlined"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<Search className="h-4 w-4" />}
                sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
              >
                Search
              </Button>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ color: '#46955A', '&:hover': { backgroundColor: '#f0f9f4' }, '&:disabled': { color: '#9ca3af' } }}
                >
                  <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={exportToExcel}
                  disabled={customers.length === 0}
                  sx={{ color: '#46955A', '&:hover': { backgroundColor: '#f0f9f4' }, '&:disabled': { color: '#9ca3af' } }}
                >
                  <Download className="h-5 w-5" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="relative overflow-auto" style={{ height: 500 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${CUSTOMER_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  const prev = pageSizeBeforeGroupRef.current;
                  setFiltersByField({});
                  setSortField('');
                  setSortDirection('asc');
                  setGroupField(null);
                  pageSizeBeforeGroupRef.current = null;
                  setPage(1);
                  if (prev && prev.pageSize !== pageSize) {
                    setPageSize(prev.pageSize);
                  }
                  fetchCustomers({
                    page: 1,
                    pageSize: prev?.pageSize ?? pageSize,
                    sortField: '',
                    sortDirection: 'asc',
                    filters: {},
                  });
                }}
                className="text-primary hover:underline"
              >
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={CUSTOMER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="w-full min-w-[1100px] table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-[4%] px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                {CUSTOMER_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={CUSTOMER_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-6 text-center text-gray-500">
                    {activeSearchKeyword ? `No customers found for "${activeSearchKeyword}"` : 'No customers available'}
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-3 py-2">
                            <GroupHeaderLabel
                              title={CUSTOMER_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((customer, index) => {
                      serial += 1;
                      block.push(
                        <tr
                          key={customer.Id || `${group.key}-${index}`}
                          className="cursor-context-menu hover:bg-gray-50"
                          onContextMenu={(e) => handleContextMenu(e, customer)}
                        >
                          <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {columnVisibility.name !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">{customer.Name || 'N/A'}</td>
                          )}
                          {columnVisibility.mobileno1 !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.MobileNo1 || 'N/A'}</td>
                          )}
                          {columnVisibility.mobileno2 !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.MobileNo2 || 'N/A'}</td>
                          )}
                          {columnVisibility.telno !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.TelNo || 'N/A'}</td>
                          )}
                          {columnVisibility.emailid !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.EmailId || 'N/A'}</td>
                          )}
                          {columnVisibility.address !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.Address || 'N/A'}</td>
                          )}
                          {columnVisibility.emiratename !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.EmirateName || 'N/A'}</td>
                          )}
                          {columnVisibility.areaname !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.AreaName || 'N/A'}</td>
                          )}
                          {columnVisibility.locationname !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900">{customer.LocationName || 'N/A'}</td>
                          )}
                          {columnVisibility.actions !== false && (
                            <td className="whitespace-nowrap px-3 py-2 text-center text-sm">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleEdit(customer)}
                                  className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(customer)}
                                  className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <Menu
          open={contextMenu !== null}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '0.5rem',
              minWidth: 150,
              mt: 0.5,
            },
          }}
        >
          <MenuItem onClick={() => handleView(selectedCustomer)} sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Eye className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>View</span>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleEdit(selectedCustomer)} sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Edit className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>Edit</span>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleDelete(selectedCustomer)} sx={{ fontSize: '0.875rem', py: 1.5, color: '#DC2626', '&:hover': { backgroundColor: '#FEE2E2' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </Box>
          </MenuItem>
        </Menu>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0 ? '0 customers' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField
                  ? ` · grouped by ${CUSTOMER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}`
                  : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;
